import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import ManageReportDataApi from '../../Api/ApiManageReportData';
import DraggableRowRenderer from '../../Components/DraggableRowRenderer';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';

const defaultKPIs = [
  {
    id: 1,
    Category: 'Total Revenue',
    Description:
      'Total revenue generated for the year to date, according to the general ledger..Professional Services Cost',
    DesiredOutcome: 'Greater',
    DisplayFormat: 'Price',
    Group: 'Profitability',
    Name: 'Average Monthly Revenue',
    Tolerance: 1000.0,
    Priority: 'High',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 2,
    Category: 'Profit Margin',
    Description:
      'Total revenue less total expenses as a percentage of total revenue.Professional Services Cost',
    DesiredOutcome: 'Greater',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Profit Margin %',
    Tolerance: 5.0,
    Priority: 'Critical',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 3,
    Category: 'Administration',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Administration Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 4,
    Category: 'Finance & Legal',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Finance & Legal Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 5,
    Category: 'HR',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Human Resources Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 6,
    Category: 'Information Technology',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Information Technology Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 7,
    Category: 'Marketing & Comms',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Marketing & Comms Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 8,
    Category: 'Plant & Equipment',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Plant & Equipment Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 9,
    Category: 'Technical Salaries',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Technical Salaries Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 10,
    Category: 'Professional Services',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Percent',
    Group: 'Profitability',
    Name: 'Professional Services Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 11,
    Category: 'Current Ratio',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: null,
    Group: 'Liquidity',
    Name: 'Current Ratio',
    Tolerance: 0.5,
    Priority: 'Critical',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 12,
    Category: 'Cash at Bank',
    Description: '',
    DesiredOutcome: 'Greater',
    DisplayFormat: 'Price',
    Group: 'Liquidity',
    Name: 'Cash at Bank',
    Tolerance: 0.0,
    Priority: 'Critical',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 13,
    Category: 'Days Outstanding',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Day',
    Group: 'Liquidity',
    Name: 'Days Outstanding',
    Tolerance: 14.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 14,
    Category: 'Total Debt to Equity',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: null,
    Group: 'Liquidity',
    Name: 'Total Debt to Equity',
    Tolerance: 0.5,
    Priority: 'High',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 15,
    Category: 'Fees / Chargeable Hour',
    Description: 'Fees / Chargeable Hour',
    DesiredOutcome: 'Greater',
    DisplayFormat: 'Price',
    Group: 'Efficiency',
    Name: 'Fees / Chargeable Hour',
    Tolerance: 5.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 16,
    Category: 'Costs / Chargeable Hour',
    Description: '',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Price',
    Group: 'Efficiency',
    Name: 'Costs / Chargeable Hour',
    Tolerance: 5.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 17,
    Category: 'Revenue / Person',
    Description: '',
    DesiredOutcome: 'Greater',
    DisplayFormat: 'Price',
    Group: 'Efficiency',
    Name: 'Revenue / Person',
    Tolerance: 1000.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 18,
    Category: '% Non Chargeable',
    Description: '% Non-Chargeable',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Price',
    Group: 'Efficiency',
    Name: '% Non Chargeable',
    Tolerance: 5.0,
    Priority: 'Low',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 19,
    Category: 'Miscellaneous',
    Description: 'Miscellaneous',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Price',
    Group: 'Profitability',
    Name: 'Miscellaneous',
    Tolerance: 5.0,
    Priority: 'Low',
    FinancialYear: new Date().getFullYear() - 1,
  },
  {
    id: 20,
    Category: 'Project Cost',
    Description: 'Project Cost',
    DesiredOutcome: 'Less',
    DisplayFormat: 'Price',
    Group: 'Profitability',
    Name: 'Project Cost',
    Tolerance: 0.0,
    Priority: 'Medium',
    FinancialYear: new Date().getFullYear() - 1,
  },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const years = [
  new Date().getFullYear() - 2,
  new Date().getFullYear() - 1,
  new Date().getFullYear(),
  new Date().getFullYear() + 1,
  new Date().getFullYear() + 2,
];

function KeyPerformanceIndicatorTable({
  name,
  company,
  rows,
  onClickedDelete,
  getTemplateData,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    Group: '',
    Category: '',
    Name: '',
    Description: '',
    Priority: '',
    Target: '',
    Tolerance: '',
    DesiredOutcome: 'All',
    DisplayFormat: 'All',
    enabled: false,
  });
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedKpis, setSelectedKpis] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addKpiList, setAddKpiList] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'Group':
      case 'Category':
      case 'Name':
      case 'Description':
      case 'Priority':
      case 'DesiredOutcome':
      case 'DisplayFormat':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'Sort':
      case 'Target':
      case 'Tolerance':
        return (a, b) => {
          const va = a[sortColumn] == null ? (sortColumn === 'Sort' ? Infinity : 0) : Number(a[sortColumn]);
          const vb = b[sortColumn] == null ? (sortColumn === 'Sort' ? Infinity : 0) : Number(b[sortColumn]);
          return va - vb;
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    requestAnimationFrame(function () {
      const event = new UIEvent('resize', {
        bubbles: true,
        cancelable: false,
        view: window
      });
      window.dispatchEvent(event);
    });
  }, []);

  useEffect(() => {
    if (company) {
      if (company.FiscalYearStartMonth > new Date().getMonth() + 1) {
        setSelectedYear(new Date().getFullYear());
      } else {
        setSelectedYear(new Date().getFullYear() + 1);
      }
    }
  }, [company]);

  const fillAutoFillFY = () => {
    ManageReportDataApi.autoFillFyKPIs(selectedYear - 1)
      .then((res) => {
        if (res.status === 200) {
          setTableRows(res.data);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  useEffect(() => {
    let add_kpi_list = [];
    let yearRows = rows.filter((row) => row.FinancialYear === selectedYear);
    defaultKPIs.forEach((kpi) => {
      let existKpi = yearRows.find(
        (row) =>
          row.Group === kpi.Group &&
          row.Category === kpi.Category &&
          row.Name === kpi.Name,
      );
      if (!existKpi) {
        add_kpi_list.push(kpi);
      }
    });

    if (yearRows.length === 0) {
      rows.forEach((row) => {
        if (row.FinancialYear === selectedYear - 1) {
          yearRows.push({
            ...row,
            FinancialYear: selectedYear,
          });
        }
      });

      if (yearRows.length > 0) fillAutoFillFY();
    }

    setTableRows(yearRows);
    setAddKpiList(add_kpi_list);

    return () => {
      setTableRows([]);
    };
    // eslint-disable-next-line
  }, [rows, selectedYear]);

  const handleChangeYear = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleSelectDefaultKpis = (event) => {
    setSelectedKpis(event.target.value);
  };

  const handleAddCheckedKpi = () => {
    if (selectedKpis.length === 0) {
      toast.warning('Please select the kpis to add.');
      return;
    }
    let add_default_kpis = defaultKPIs.filter(
      (kpi) => selectedKpis.indexOf(kpi.id) !== -1,
    );

    add_default_kpis.map((kpi) => (kpi.FinancialYear = selectedYear));
    ManageReportDataApi.addDefaultKpis(add_default_kpis)
      .then((res) => {
        if (res.status === 200) {
          getTemplateData(name);
          setDialogOpen(false);
          setSelectedKpis([]);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedRowIds(ids);
    let selected_indexes = [];
    tableRows.forEach((row, index) => {
      if (ids.has(row.id)) {
        selected_indexes.push(index);
      }
    });
    setSelectedIndexes(selected_indexes);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (
      field.column.key === 'Target' ||
      field.column.key === 'DesiredOutCome' ||
      field.column.key === 'Tolerance'
    ) {
      if (updated_data.updated[field.column.key] === '')
        updated_data.updated[field.column.key] = '0';
    }

    if (field.column.key !== 'DisplayFormat') {
      if (updated_data.updated[field.column.key] === '') {
        toast.warning('Please insert the column value.');
        return;
      }
    }

    if (selectedRowIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedRowIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    setTableRows(updatedRows);
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const renderRow = useCallback(
    (key, props) => {
      async function onRowReorder(fromIndices, toIndex) {
        const updatedRows = [...tableRows];
        let draggedRows = [];
        fromIndices.forEach((index) => draggedRows.push(updatedRows[index]));
        for (let i = fromIndices.length - 1; i >= 0; i--) {
          updatedRows.splice(fromIndices[i], 1);
        }

        updatedRows.splice(toIndex, 0, ...draggedRows);

        let updateData = [];

        updatedRows.map((row, index) => {
          updateData.push({
            id: row.id,
            Sort: index + 1,
          });
          row.Sort = index + 1;
          return row;
        });

        setTableRows(updatedRows);
        setSelectedRowIds(new Set());
        setSelectedIndexes([]);

        ManageReportDataApi.updateKpiSort(updateData)
          .then((res) => {
            if (res.status === 200) {
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      }

      return (
        <DraggableRowRenderer
          key={key}
          {...props}
          selectedIndexes={selectedIndexes}
          onRowReorder={onRowReorder}
        />
      );
    },
    [selectedIndexes, tableRows],
  );

  const handleClickDelete = () => {
    if (selectedRow) {
      onClickedDelete(name, [selectedRow.id]);
      setSelectedRow(null);
    } else {
      if (selectedRowIds.size > 0) {
        onClickedDelete(name, Array.from(selectedRowIds));
        setSelectedRowIds(new Set());
        setSelectedIndexes([]);
      }
    }
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const tableColumns = useMemo(() => {
    return [
      SelectColumn,
      {
        key: 'Sort',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Sort</b>
            {sortColumns.find((column) => column.columnKey === 'Sort')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Sort')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 75,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Sort}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Sort: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Sort}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Sort: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Group',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Group</b>
            {sortColumns.find((column) => column.columnKey === 'Group')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Group')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 100,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Group}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Group: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Category',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Category</b>
            {sortColumns.find((column) => column.columnKey === 'Category')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Category')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 150,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Category}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Category: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Name',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Name</b>
            {sortColumns.find((column) => column.columnKey === 'Name')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Name')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 150,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Description',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Description</b>
            {sortColumns.find((column) => column.columnKey === 'Description')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Description')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 300,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Description}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Description: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Priority',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Priority</b>
            {sortColumns.find((column) => column.columnKey === 'Priority')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Priority')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        width: 75,
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Priority}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Priority: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Target',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Target</b>
            {sortColumns.find((column) => column.columnKey === 'Target')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Target')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 75,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Target}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Target: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Target}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Target: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'DesiredOutcome',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Desired Outcome</b>
            {sortColumns.find((column) => column.columnKey === 'DesiredOutcome')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DesiredOutcome',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 125,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DesiredOutcome}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DesiredOutcome: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                <option value="Less">Less</option>
                <option value="Greater">Greater</option>
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              variant="standard"
              value={props.row.DesiredOutcome ? props.row.DesiredOutcome : ''}
              onChange={(ev) =>
                props.onRowChange(
                  { ...props.row, DesiredOutcome: ev.target.value },
                  true,
                )
              }
              fullWidth
            >
              <MenuItem value="Less">Less</MenuItem>
              <MenuItem value="Greater">Greater</MenuItem>
            </Select>
          );
        },
      },
      {
        key: 'Tolerance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Tolerance</b>
            {sortColumns.find((column) => column.columnKey === 'Tolerance')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Tolerance')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 150,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Tolerance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Tolerance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Tolerance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Tolerance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'DisplayFormat',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Display Format</b>
            {sortColumns.find((column) => column.columnKey === 'DisplayFormat')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DisplayFormat',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 125,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DisplayFormat}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DisplayFormat: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                <option value="Percent">Percent</option>
                <option value="Price">Price</option>
                <option value="Day">Day</option>
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              variant="standard"
              value={props.row.DisplayFormat ? props.row.DisplayFormat : ''}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DisplayFormat: e.target.value },
                  true,
                )
              }
              fullWidth
            >
              <MenuItem value="">&nbsp;</MenuItem>
              <MenuItem value="Percent">Percent</MenuItem>
              <MenuItem value="Price">Price</MenuItem>
              <MenuItem value="Day">Day</MenuItem>
            </Select>
          );
        },
      },
      {
        key: 'Enable',
        name: 'Enable',
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.Enable}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, Enable: e.target.checked },
                  true,
                )
              }
              color="success"
            />
          </div>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [tableRows, sortColumns]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.Group
          ? r.Group?.toLowerCase().includes(filters.Group.toLowerCase())
          : true) &&
        (filters.Category
          ? r.Category?.toLowerCase().includes(filters.Category.toLowerCase())
          : true) &&
        (filters.Name
          ? r.Name?.toLowerCase().includes(filters.Name.toLowerCase())
          : true) &&
        (filters.Description
          ? r.Description?.toLowerCase().includes(
              filters.Description.toLowerCase(),
            )
          : true) &&
        (filters.Priority
          ? r.Priority?.toLowerCase().includes(filters.Priority.toLowerCase())
          : true) &&
        (filters.Target
          ? r.Target?.toString().includes(filters.Target.toString())
          : true) &&
        (filters.Tolerance
          ? r.Tolerance?.toString().includes(filters.Tolerance.toString())
          : true) &&
        (filters.DesiredOutcome !== 'All'
          ? r.DesiredOutcome === filters.DesiredOutcome
          : true) &&
        (filters.DisplayFormat !== 'All'
          ? r.DisplayFormat === filters.DisplayFormat
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);
  
  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: 9,
          zIndex: 10,
          height: 45,
        }}
      >
        <label style={{ margin: 8 }}>Financial Year :</label>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
          <Select value={selectedYear} onChange={handleChangeYear}>
            {years &&
              years.map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <IconButton
          aria-label="Add_Modal"
          onClick={() => {
            setDialogOpen(true);
          }}
        >
          <AddCircleOutlineIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DndProvider backend={HTML5Backend}>
          <DataGrid
            rowKeyGetter={rowKeyGetter}
            className={
              filters.enabled
                ? 'filter-container fill-grid rdg-light'
                : 'fill-grid rdg-light'
            }
            headerRowHeight={filters.enabled ? 70 : 50}
            columns={tableColumns}
            rows={sortedRows}
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            renderers={{ renderRow }}
            selectedRows={selectedRowIds}
            onSelectedRowsChange={handleClickRow}
            onRowsChange={handleGridRowsUpdated}
            onCellClick={handleCellClick}
            rowHeight={50}
            enableVirtualization={true}
            style={{ maxHeight: window.innerHeight - 145 }}
          />
        </DndProvider>
      </FilterContext.Provider>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      >
        <DialogTitle>Add KeyPerformance Indicator</DialogTitle>
        <DialogContent>
          <FormControl>
            <InputLabel>Default Kpis</InputLabel>
            <Select
              multiple
              fullWidth
              value={selectedKpis}
              style={{ margin: 12, minWidth: 500 }}
              input={<Input />}
              MenuProps={MenuProps}
              onChange={(e) => handleSelectDefaultKpis(e)}
            >
              {addKpiList.map((kpi, index) => (
                <MenuItem
                  key={index}
                  value={kpi.id}
                  disabled={selectedKpis.indexOf(kpi.id) !== -1 ? true : false}
                >
                  {kpi.Name} - {kpi.Description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            style={{
              backgroundColor: '#e0e0e0',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
            onClick={() => {
              setDialogOpen(false);
              setSelectedKpis([]);
            }}
          >
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor: '#1976d2',
              color: '#ffffff',
            }}
            onClick={handleAddCheckedKpi}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

KeyPerformanceIndicatorTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getTemplateData: PropTypes.func.isRequired,
};

export default KeyPerformanceIndicatorTable;
